import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Eye, Code, Globe, ArrowUpRight, TrendingUp, Network, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { relativeTime } from "@/lib/utils";

export default function SignalNetworkPage() {
  const [summary, setSummary] = useState(null);
  const [views, setViews] = useState([]);
  const [days, setDays] = useState(7);

  const load = async (d = days) => {
    const [s, v] = await Promise.all([
      api.get(`/signal-network/summary?days=${d}`),
      api.get("/signal-network/recent?limit=30"),
    ]);
    setSummary(s.data); setViews(v.data.views || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const clearData = async () => {
    if (!window.confirm("Wipe ALL Signal Network telemetry (including leftover test data)? This can't be undone.")) return;
    try {
      const { data } = await api.delete("/signal-network/data");
      toast.success(`Cleared ${data.deleted_telemetry} signal records`);
      load();
    } catch { toast.error("Couldn't clear — owner only"); }
  };

  const copyEmbedSnippet = (company) => {
    const url = `${window.location.origin}/score/${encodeURIComponent(company)}?embed=1`;
    const html = `<iframe src="${url}" width="380" height="220" style="border:1px solid #ddd;border-radius:6px"></iframe>`;
    navigator.clipboard.writeText(html);
    toast.success("Embed snippet copied");
  };

  if (!summary) return <DashboardLayout title="..."><div className="text-sm text-muted-foreground">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout
      eyebrow="Distribution / Intent Graph"
      title="Signal Network"
      actions={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {[7, 14, 30].map((d) => (
              <button key={d} onClick={() => { setDays(d); load(d); }} data-testid={`signal-range-${d}`}
                className={`px-3 py-1 rounded-sm text-xs border transition-colors ${
                  days === d ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
                }`}>{d}d</button>
            ))}
          </div>
          <button onClick={clearData} data-testid="signal-clear-btn"
            className="px-3 py-1 rounded-sm text-xs border border-border text-muted-foreground hover:border-destructive/50 hover:text-destructive transition-colors inline-flex items-center gap-1">
            <Trash2 className="w-3 h-3" /> Clear data
          </button>
        </div>
      }
    >
      <div className="data-card mb-6 flex items-start gap-3 bg-card/40">
        <Network className="w-5 h-5 text-signal-500 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-semibold text-base">The Logistics Intent Graph</div>
          <div className="text-sm text-muted-foreground mt-1">
            Every embed of the Signal Score widget becomes a tracked intent signal — the host domain, the company being scored, and the referring page. This is your inbound moat: external companies generate intelligence that flows back into LogiGrowth.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 grid-borders bg-card/30 mb-8">
        <Stat label={`Views (${days}d)`} value={summary.totals.views} icon={Eye} testid="signal-stat-views" />
        <Stat label="Embed loads" value={summary.totals.embeds} icon={Code} tone="signal" testid="signal-stat-embeds" />
        <Stat label="Unique companies" value={summary.totals.unique_companies} icon={Globe} testid="signal-stat-companies" />
        <Stat label="Embed rate" value={`${summary.totals.views ? Math.round(summary.totals.embeds / summary.totals.views * 100) : 0}%`} icon={TrendingUp} testid="signal-stat-rate" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <section>
          <div className="label-eyebrow mb-3">Top scored companies</div>
          <div className="border border-border rounded-sm overflow-hidden">
            {summary.top_companies.length === 0 && <div className="p-8 text-center text-xs text-muted-foreground">No data yet — share /score/{"<company-name>"} links.</div>}
            {summary.top_companies.map((c, i) => (
              <div key={i} className="grid grid-cols-12 px-4 py-3 border-t first:border-t-0 border-border items-center" data-testid={`signal-company-${i}`}>
                <div className="col-span-5">
                  <Link to={`/score/${encodeURIComponent(c.company)}`} target="_blank" className="font-medium text-sm hover:underline inline-flex items-center gap-1">{c.company} <ArrowUpRight className="w-3 h-3" /></Link>
                </div>
                <div className="col-span-2 text-right"><span className="font-mono text-base font-semibold text-signal-500">{c.score}</span></div>
                <div className="col-span-2 text-right font-mono text-xs">{c.views} views</div>
                <div className="col-span-2 text-right font-mono text-xs text-amber-accent">{c.embeds} embeds</div>
                <div className="col-span-1 text-right">
                  <button onClick={() => copyEmbedSnippet(c.company)} className="text-[0.6rem] tracking-widest uppercase text-muted-foreground hover:text-foreground" data-testid={`signal-copy-${i}`}>Copy</button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="label-eyebrow mb-3">Top referrers</div>
          <div className="border border-border rounded-sm overflow-hidden">
            {summary.top_referrers.length === 0 && <div className="p-8 text-center text-xs text-muted-foreground">No external referrers yet.</div>}
            {summary.top_referrers.map((r, i) => (
              <div key={i} className="grid grid-cols-12 px-4 py-3 border-t first:border-t-0 border-border items-center" data-testid={`signal-referrer-${i}`}>
                <div className="col-span-9 font-mono text-xs truncate">{r.host}</div>
                <div className="col-span-3 text-right font-mono text-xs">{r.views}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section>
        <div className="label-eyebrow mb-3">Recent score views (live)</div>
        <div className="data-card max-h-80 overflow-y-auto">
          {views.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">No views yet.</div>}
          <ul className="space-y-1.5 text-xs font-mono">
            {views.map((v, i) => (
              <li key={v.id} className="grid grid-cols-12 gap-2 border-b border-border last:border-0 py-1.5" data-testid={`signal-view-${i}`}>
                <div className="col-span-3 truncate"><span className="text-signal-500">{v.company}</span></div>
                <div className="col-span-1 text-signal-500">{v.score}</div>
                <div className="col-span-4 text-muted-foreground truncate">{v.referrer_host || v.origin_host || "direct"}</div>
                <div className="col-span-2 text-muted-foreground">{v.embed ? "embed" : "view"}</div>
                <div className="col-span-2 text-right text-muted-foreground">{relativeTime(v.at)}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="mt-8 p-4 border border-border rounded-sm bg-muted/20">
        <div className="label-eyebrow mb-2">Embed snippet (drop on any site)</div>
        <pre className="text-xs font-mono bg-card/60 border border-border rounded-sm p-3 overflow-x-auto" data-testid="signal-snippet">{`<iframe src="${window.location.origin}/score/YOUR_COMPANY?embed=1"
        width="380" height="220"
        style="border:1px solid #ddd;border-radius:6px"></iframe>`}</pre>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, sub, icon: Icon, tone, testid }) {
  const c = tone === "signal" ? "text-signal-500" : tone === "amber" ? "text-amber-accent" : "";
  return (
    <div className="p-5" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2"><Icon className="w-3 h-3 text-muted-foreground" /><span className="label-eyebrow">{label}</span></div>
      <div className={`text-3xl font-display font-semibold tracking-tightest ${c}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
